'use client';

import { type CSSProperties, Fragment, type ReactNode, useId, useMemo } from 'react';

import clsx from 'clsx';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { Area, ComposedChart, Line as ReLine, Tooltip as ReTooltip } from 'recharts';
import { type NameType, type ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { type TooltipContentProps } from 'recharts/types/component/Tooltip';
import { type CurveType } from 'recharts/types/shape/Curve';
import { type AxisDomain } from 'recharts/types/util/types';

import { useXAxis, type XLabelFormatter } from '@/components/common/charts/hooks/use-x-axis';
import { useYAxis, type YLabelFormatter } from '@/components/common/charts/hooks/use-y-axis';
import { Legend, type LegendProps, type LegendVariant } from '@/components/common/charts/Legend';
import { ResponsiveContainer } from '@/components/common/charts/ResponsiveContainer';
import { Tooltip } from '@/components/common/charts/Tooltip';
import { type DateXAxisType, type XAxisType, type XValue } from '@/components/common/charts/types';
import { type WithClassName } from '@/types/react';
import { type RegularColor } from '@/types/theme';
import { mapStringToHighContrastColor } from '@/utils/color';
import { getCssSizeValue } from '@/utils/css';
import { getColorCssValue } from '@/utils/theme';

import styles from './LineChart.module.scss';

export type CommonLineProps = {
  curveType?: CurveType;
  dot?: boolean;
};

type LinePoint<XType extends XAxisType> = {
  x: XValue<XType>;
  y: number;
  meta?: Record<string, string | number | null | undefined>;
};

export type Line<XType extends XAxisType> = CommonLineProps & {
  id: string;
  name: string;
  points: LinePoint<XType>[];
  color?: RegularColor;
  strokeDasharray?: string;
  withFill?: boolean;
  legendVariant?: LegendVariant;
};

type XAxisProps<XType extends XAxisType> = {
  xAxisType: XType;
};

export type LineChartProps<XType extends XAxisType> = WithClassName &
  CommonLineProps & {
    lines: Line<XType>[];
    withLegend?: boolean;
    formatXLabel?: XLabelFormatter<XType>;
    withXAxis?: boolean;
    xAxisDomain?: AxisDomain;
    withYAxis?: boolean;
    yAxisWidth?: number;
    yAxisDomain?: AxisDomain;
    yTicks?: number[];
    height?: number | string;
    formatYLabel?: YLabelFormatter;
    renderTooltip?: (props: TooltipContentProps<ValueType, NameType>) => ReactNode;
    legendDirection?: LegendProps['direction'];
    legendClassName?: string;
    legendWrap?: LegendProps['wrap'];
    legendGap?: number;
  } & (XType extends DateXAxisType ? Partial<XAxisProps<XType>> : XAxisProps<XType>);

const getXAxisPoints = <XType extends XAxisType>(lines: Line<XType>[]): XValue<XType>[] => {
  return sortBy(uniq(lines.flatMap((line) => line.points.map((point) => point.x))));
};

const getChartData = <XType extends XAxisType>(xAxisPoints: XValue<XType>[], lines: Line<XType>[]) => {
  return xAxisPoints.map((xPoint) => {
    const pointData: Record<string, string | number> = {
      x: xPoint.valueOf(),
    };

    lines.forEach((line) => {
      const point = line.points.find((point) => point.x.valueOf() === xPoint.valueOf());

      if (point) {
        pointData[`line-${line.id}`] = point.y;

        if (point.meta) {
          Object.entries(point.meta).forEach(([key, value]) => {
            if (value != null) {
              pointData[key] = value;
            }
          });
        }
      }
    });

    return pointData;
  });
};

export const LineChart = <XType extends XAxisType>(props: LineChartProps<XType>) => {
  const {
    className,
    lines,
    xAxisType = 'datetime',
    withLegend,
    curveType = 'monotone',
    dot = false,
    formatXLabel,
    withXAxis = true,
    xAxisDomain,
    withYAxis = true,
    yAxisWidth,
    yAxisDomain,
    yTicks,
    height,
    formatYLabel,
    renderTooltip,
    legendDirection,
    legendClassName,
    legendWrap,
    legendGap,
  } = props;

  const chartId = useId();

  const linesWithColor = useMemo(() => {
    return lines.map((line) => ({
      ...line,
      color: line.color ?? mapStringToHighContrastColor(line.id),
    }));
  }, [lines]);

  const xAxisPoints = useMemo(() => {
    return getXAxisPoints(lines);
  }, [lines]);

  const chartData = useMemo(() => {
    return getChartData(xAxisPoints, lines);
  }, [xAxisPoints, lines]);

  const yValues = useMemo(() => {
    return linesWithColor.map(({ points }) => points.map((point) => point.y));
  }, [linesWithColor]);

  const fillColorIds = useMemo(() => {
    return linesWithColor.reduce<Partial<Record<string, string>>>((colorIds, line) => {
      colorIds[line.id] = `${chartId}-fillLineColor-${line.id}`;

      return colorIds;
    }, {});
  }, [chartId, linesWithColor]);

  const strokeColorIds = useMemo(() => {
    return linesWithColor.reduce<Partial<Record<string, string>>>((colorIds, line) => {
      colorIds[line.id] = `${chartId}-strokeLineColor-${line.id}`;

      return colorIds;
    }, {});
  }, [chartId, linesWithColor]);

  const colorsStyle = useMemo(() => {
    const style: CSSProperties = {};

    linesWithColor.forEach(({ id, color }) => {
      style[`--line-${id}-color`] = getColorCssValue(color);
    });

    return style;
  }, [linesWithColor]);

  const xAxis = useXAxis<XType>({
    type: xAxisType as XType,
    values: xAxisPoints,
    domain: xAxisDomain,
    formatter: formatXLabel,
  });

  const yAxis = useYAxis({
    values: yValues,
    formatter: formatYLabel,
    width: yAxisWidth,
    domain: yAxisDomain,
    ticks: yTicks,
  });

  return (
    <div
      className={clsx(styles.root, className)}
      style={{
        '--line-chart-height': getCssSizeValue(height),
      }}
    >
      <ResponsiveContainer className={styles.chart}>
        <ComposedChart data={chartData}>
          <defs>
            {linesWithColor.map(({ id, color }) => (
              <Fragment key={id}>
                <linearGradient id={fillColorIds[id]} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={getColorCssValue(color)} stopOpacity={0.26} />
                  <stop offset="100%" stopColor={getColorCssValue(color)} stopOpacity={0} />
                </linearGradient>

                <linearGradient id={strokeColorIds[id]} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={getColorCssValue(color)} stopOpacity={1} />
                  <stop offset="100%" stopColor={getColorCssValue(color)} stopOpacity={0} />
                </linearGradient>
              </Fragment>
            ))}
          </defs>

          {withXAxis && xAxis}
          {withYAxis && yAxis}

          {linesWithColor.map(
            ({ id, name, color, curveType: lineCurveType, dot: lineDot, strokeDasharray, withFill = true }) => {
              const seriesProps = {
                dataKey: `line-${id}`,
                name,
                type: lineCurveType ?? curveType,
                dot: lineDot ?? dot,
                isAnimationActive: false,
              };

              if (withFill) {
                return (
                  <Area
                    key={id}
                    {...seriesProps}
                    fill={`url(#${fillColorIds[id]})`}
                    stroke={`url(#${strokeColorIds[id]})`}
                    strokeDasharray={strokeDasharray}
                  />
                );
              }

              return (
                <ReLine
                  key={id}
                  {...seriesProps}
                  fill="none"
                  stroke={getColorCssValue(color)}
                  strokeDasharray={strokeDasharray}
                  strokeWidth={2}
                />
              );
            }
          )}

          <ReTooltip
            shared={false}
            cursor={false}
            content={(tooltipProps) => (renderTooltip ? renderTooltip(tooltipProps) : <Tooltip {...tooltipProps} />)}
            isAnimationActive={false}
            wrapperStyle={colorsStyle}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {withLegend && (
        <Legend
          className={clsx(styles.legend, legendClassName)}
          direction={legendDirection}
          wrap={legendWrap}
          gap={legendGap}
          datasets={linesWithColor.map(({ id, name, color, legendVariant }) => ({
            id,
            name,
            color,
            variant: legendVariant,
          }))}
        />
      )}
    </div>
  );
};
